import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';


const UserProfile: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setName(response.data.name);
      setEmail(response.data.email);
    } catch (err) {
      console.error('Failed to load user:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (password && password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    setLoading(true);

    try {
      const updateData: any = { name, email };
      if (password) {
        updateData.password = password;
      }

      await axios.put(`${API_URL}/api/auth/me`, updateData);
      
      setMessage({ type: 'success', text: 'Profile updated successfully' });
      setPassword('');
      setConfirmPassword('');
      
      // Reload user data
      await loadUser();
      
      // Update localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...userData, name, email }));
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 p-6">
      <h2 className="text-xl font-sans font-bold normal-case text-gray-800 dark:text-white mb-6">User Profile</h2>

      {message && (
        <div className={`mb-4 px-4 py-3 rounded border font-sans ${
          message.type === 'success'
            ? 'bg-green-300 border-green-400 text-gray-800'
            : 'bg-red-300 border-red-400 text-gray-800'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-white font-sans mb-2">
            Username
          </label>
          <input
            type="text"
            value={user.username}
            disabled
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed font-sans"
          />
          <p className="text-xs text-gray-500 dark:text-gray-300 mt-1 font-sans">Username cannot be changed</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-white font-sans mb-2">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-white font-sans mb-2">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
            required
          />
        </div>

        <div className="border-t border-gray-200 dark:border-gray-500 pt-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white font-sans normal-case mb-4">Change Password</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-white font-sans mb-2">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Leave blank to keep current password"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-white font-sans mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-500 rounded bg-white dark:bg-gray-700 text-gray-800 dark:text-white font-sans focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Confirm your new password"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-400 hover:bg-blue-500 text-gray-800 font-semibold font-sans py-3 rounded transition disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

export default UserProfile;
